<template>
    <div class="flex items-center justify-between gap-4 px-5 py-3 bg-[#F1F1FF]">
        <div class="flex items-center gap-2">
            <i class="bi bi-stars text-indigo-500 text-base" aria-hidden="true" />
            <span class="body-small text-valence-grey-850">Was this insight helpful?</span>
        </div>
        <div class="flex items-center gap-2">
            <BaseButton size="sm" :theme="displayValue === 1 ? 'secondary' : 'neutral'" :disabled="disabled" data-testid="insight-feedback-yes" @click="handleYes">
                Yes
            </BaseButton>
            <BaseButton size="sm" :theme="displayValue === 0 ? 'secondary' : 'neutral'" :disabled="disabled" data-testid="insight-feedback-no" @click="handleNo"> No </BaseButton>
        </div>
    </div>

    <BaseDialog v-model:open="showFeedbackModal" title="Tell us more" dialog-content-class="max-w-lg w-full">
        <div class="flex flex-col gap-4 p-6">
            <div class="flex items-center justify-between">
                <h3 class="title-small text-valence-grey-850">
                    {{ pendingValue === 1 ? "What did you find helpful?" : "Why wasn't this helpful?" }}
                </h3>
                <button type="button" aria-label="Close" class="text-gray-400 hover:text-gray-600 transition-colors hover:cursor-pointer" @click="handleDismissModal">
                    <i class="bi bi-x-lg text-xl" aria-hidden="true" />
                </button>
            </div>
            <div class="flex flex-col gap-2">
                <p class="body-small text-valence-grey-700 pb-2">Select all that apply</p>
                <div class="grid grid-cols-2 gap-x-6 gap-y-2">
                    <label
                        v-for="issue in availableIssues"
                        :key="issue"
                        :for="`feedback-tag-${issue}`"
                        class="body-small flex cursor-pointer items-center gap-3 text-valence-grey-600">
                        <input
                            :id="`feedback-tag-${issue}`"
                            v-model="selectedTags"
                            type="checkbox"
                            :value="issue"
                            class="h-5 w-5 shrink-0 cursor-pointer rounded-md border border-valence-grey-250 accent-valence-primary-500" />
                        {{ issue }}
                    </label>
                </div>
            </div>
            <BaseTextarea
                v-model:input="comment"
                rows="3"
                placeholder="Anything else you'd like to share?"
                disable-autosize
                class="resize-none rounded-md border border-valence-grey-250 p-3 text-valence-grey-850 focus-visible:border-valence-primary-500" />
            <div class="flex justify-end">
                <BaseButton size="sm" theme="primary" :disabled="(!selectedTags.length && !comment.trim()) || feedbackSubmitting" @click="handleSubmitFeedback">Submit</BaseButton>
            </div>
        </div>
    </BaseDialog>

    <BaseNotification v-if="showError" theme="error" text="Something went wrong. Please try again." @dismiss="showError = false" />
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { computed, ref, watch } from "vue";

const POSITIVE_TAGS = ["Felt true", "Showed me something new", "Felt personalized", "Gave me a next step", "Made me think"];
const NEGATIVE_TAGS = ["Too generic", "Got me wrong", "Missed important context", "Too obvious", "Not actionable", "Too vague", "Shouldn't have been generated"];

interface Props {
    feedbackValue: 0 | 1 | null;
    feedbackTags: string[];
    feedbackComment: string | null;
    disabled?: boolean;
    feedbackSubmitting?: boolean;
    submitError?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    feedbackSubmitting: false,
    submitError: false,
});

const emit = defineEmits<{
    select: [value: 0 | 1];
    submitFeedback: [payload: { tags: string[]; comment: string | null; value: 0 | 1 }];
}>();

const showFeedbackModal = ref(false);
const showError = ref(false);
const pendingValue = ref<0 | 1 | null>(null);
const selectedTags = ref<string[]>([]);
const comment = ref("");
const optimisticValue = ref<0 | 1 | null>(null);

const displayValue = computed(() => optimisticValue.value ?? props.feedbackValue);

// Clear optimistic value once the server response arrives
watch(
    () => props.feedbackValue,
    () => {
        optimisticValue.value = null;
    },
);

watch(
    () => props.submitError,
    (error) => {
        if (error) {
            optimisticValue.value = null;
            showError.value = true;
        }
    },
);

const availableIssues = computed(() => (pendingValue.value === 1 ? POSITIVE_TAGS : NEGATIVE_TAGS));

function handleYes() {
    if (props.disabled || props.feedbackSubmitting) {
        return;
    }
    pendingValue.value = 1;
    optimisticValue.value = 1;
    if (props.feedbackValue !== 1) {
        emit("select", 1);
    }
    selectedTags.value = props.feedbackValue === 1 ? [...props.feedbackTags] : [];
    comment.value = props.feedbackValue === 1 ? (props.feedbackComment ?? "") : "";
    showFeedbackModal.value = true;
}

function handleNo() {
    if (props.disabled || props.feedbackSubmitting) {
        return;
    }
    pendingValue.value = 0;
    optimisticValue.value = 0;
    if (props.feedbackValue !== 0) {
        emit("select", 0);
    }
    selectedTags.value = props.feedbackValue === 0 ? [...props.feedbackTags] : [];
    comment.value = props.feedbackValue === 0 ? (props.feedbackComment ?? "") : "";
    showFeedbackModal.value = true;
}

function handleDismissModal() {
    showFeedbackModal.value = false;
}

function handleSubmitFeedback() {
    if (pendingValue.value === null) {
        return;
    }
    emit("submitFeedback", { tags: selectedTags.value, comment: comment.value.trim() || null, value: pendingValue.value });
    showFeedbackModal.value = false;
}
</script>
